<template>
  <div class="creador-page">
    <div class="bg-grid" aria-hidden="true"></div>

    <div class="creador-shell">
      <!-- Header -->
      <header class="page-header fade-in">
        <div class="page-icon-box">
          <v-icon color="#F2F2ED" size="20">mdi-clipboard-edit-outline</v-icon>
        </div>
        <div>
          <h1 class="page-title">Creador de rutinas</h1>
          <p class="page-sub">Diseña rutinas de entrenamiento personalizadas</p>
        </div>
      </header>

      <!-- Stepper -->
      <section class="fade-in delay-100">
        <v-stepper v-model="pasoActual" class="dark-stepper">
          <v-stepper-header class="dark-stepper-header">
            <v-stepper-step step="1" :complete="pasoActual > 1">Información básica</v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step step="2" :complete="pasoActual > 2">Configurar días</v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step step="3" :complete="pasoActual > 3">Agregar ejercicios</v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step step="4">Revisar y guardar</v-stepper-step>
          </v-stepper-header>

          <v-stepper-items>
            <!-- Paso 1 -->
            <v-stepper-content step="1">
              <div class="card-flat">
                <div class="card-flat-head">
                  <span class="section-eyebrow">
                    <span class="tick-mark"></span>
                    Información básica de la rutina
                  </span>
                </div>
                <div class="card-flat-body">
                  <v-form ref="formPaso1" class="dark-form">
                    <div class="field-row">
                      <div class="field">
                        <label class="field-label">Nombre de la rutina *</label>
                        <v-text-field v-model="rutina.nombre" :rules="reglasRequerido" outlined dense
                          hide-details="auto" placeholder="Ej. Rutina de Fuerza Avanzada"
                          class="dark-input"></v-text-field>
                      </div>
                      <div class="field">
                        <label class="field-label">Objetivo *</label>
                        <v-select v-model="rutina.objetivo" :items="objetivos" :rules="reglasRequerido" outlined
                          dense hide-details="auto" placeholder="Seleccionar" class="dark-input"></v-select>
                      </div>
                    </div>

                    <div class="field-row">
                      <div class="field">
                        <label class="field-label">Nivel de dificultad *</label>
                        <v-select v-model="rutina.nivel_dificultad" :items="nivelesDificultad"
                          :rules="reglasRequerido" outlined dense hide-details="auto" placeholder="Seleccionar"
                          class="dark-input"></v-select>
                      </div>
                      <div class="field">
                        <label class="field-label">Duración (semanas) *</label>
                        <v-text-field v-model="rutina.duracion_semanas" type="number" min="1"
                          :rules="reglasRequerido" outlined dense hide-details="auto" placeholder="4"
                          class="dark-input"></v-text-field>
                      </div>
                    </div>

                    <div class="field">
                      <label class="field-label">Descripción</label>
                      <v-textarea v-model="rutina.descripcion" outlined dense rows="3" hide-details="auto"
                        placeholder="Describe el enfoque y beneficios de esta rutina…"
                        class="dark-input"></v-textarea>
                    </div>
                  </v-form>
                </div>
              </div>

              <div class="stepper-actions">
                <button class="btn-primary" @click="siguientePaso(1)">
                  Continuar
                  <v-icon size="15" class="ml-2">mdi-arrow-right</v-icon>
                </button>
                <button class="btn-ghost" @click="cancelar">Cancelar</button>
              </div>
            </v-stepper-content>

            <!-- Paso 2 -->
            <v-stepper-content step="2">
              <div class="card-flat">
                <div class="card-flat-head">
                  <span class="section-eyebrow">
                    <span class="tick-mark"></span>
                    Configurar días de entrenamiento
                  </span>
                </div>
                <div class="card-flat-body">
                  <p class="body-text mb-lg">Selecciona los días de la semana para esta rutina:</p>

                  <div class="chip-row mb-lg">
                    <button v-for="dia in diasSemana" :key="dia.value" class="filter-chip"
                      :class="{ 'filter-chip-active': diasSeleccionados.includes(dia.value) }"
                      @click="toggleDia(dia.value)">
                      {{ dia.text }}
                    </button>
                  </div>

                  <div class="section-divider"></div>

                  <div v-if="diasSeleccionados.length > 0">
                    <span class="mini-eyebrow">Nombres personalizados para los días</span>
                    <div class="field mt-md" v-for="diaValue in diasSeleccionados" :key="diaValue">
                      <label class="field-label">{{ obtenerNombreDia(diaValue) }}</label>
                      <v-text-field v-model="nombresDias[diaValue]" outlined dense hide-details="auto"
                        :placeholder="`Ej. ${obtenerNombrePorDefecto(diaValue)}`" class="dark-input"></v-text-field>
                    </div>
                  </div>

                  <div v-else class="info-box">
                    <v-icon size="16" color="#8C8C93" class="mr-2">mdi-information-outline</v-icon>
                    Selecciona al menos un día de entrenamiento
                  </div>
                </div>
              </div>

              <div class="stepper-actions">
                <button class="btn-primary" @click="siguientePaso(2)" :disabled="diasSeleccionados.length === 0">
                  Continuar
                  <v-icon size="15" class="ml-2">mdi-arrow-right</v-icon>
                </button>
                <button class="btn-ghost" @click="pasoActual = 1">
                  <v-icon size="15" class="mr-2">mdi-arrow-left</v-icon>
                  Atrás
                </button>
                <button class="btn-ghost" @click="cancelar">Cancelar</button>
              </div>
            </v-stepper-content>

            <!-- Paso 3 -->
            <v-stepper-content step="3">
              <div class="card-flat card-flat-nopad">
                <div class="card-flat-head">
                  <span class="section-eyebrow">
                    <span class="tick-mark"></span>
                    Agregar ejercicios por día
                  </span>
                </div>

                <v-tabs v-model="tabDiaActual" grow class="dark-tabs">
                  <v-tab v-for="diaValue in diasSeleccionados" :key="diaValue">
                    {{ nombresDias[diaValue] || obtenerNombreDia(diaValue) }}
                  </v-tab>
                </v-tabs>

                <v-tabs-items v-model="tabDiaActual" class="dark-tabs-items">
                  <v-tab-item v-for="diaValue in diasSeleccionados" :key="diaValue">
                    <div class="tab-body">
                      <button class="btn-primary mb-lg" @click="mostrarDialogoEjercicios(diaValue)">
                        <v-icon size="15" class="mr-2">mdi-plus</v-icon>
                        Agregar ejercicio
                      </button>

                      <draggable v-if="ejerciciosPorDia[diaValue]" v-model="ejerciciosPorDia[diaValue]"
                        handle=".handle" @end="reordenarEjercicios(diaValue)">
                        <div v-for="(ejercicio, index) in ejerciciosPorDia[diaValue]" :key="index"
                          class="ejercicio-card">
                          <div class="ejercicio-head">
                            <v-icon size="18" class="handle mr-md" color="#5C5C62"
                              style="cursor: move">mdi-drag-horizontal</v-icon>
                            <div class="flex-1">
                              <strong class="ejercicio-name">{{ ejercicio.nombre }}</strong>
                              <div class="ejercicio-cat">{{ ejercicio.categoria_nombre }}</div>
                            </div>
                            <div class="flex gap-xs">
                              <button class="icon-btn" @click="editarEjercicioDia(diaValue, index)">
                                <v-icon size="15" color="#8C8C93">mdi-pencil</v-icon>
                              </button>
                              <button class="icon-btn icon-btn-danger" @click="eliminarEjercicioDia(diaValue, index)">
                                <v-icon size="15" color="#FF6B6B">mdi-delete</v-icon>
                              </button>
                            </div>
                          </div>

                          <div v-if="ejercicio.configurado" class="ejercicio-config">
                            <div class="field-row field-row-3">
                              <div class="field">
                                <label class="field-label">Series</label>
                                <v-text-field v-model="ejercicio.series" type="number" min="1" outlined dense
                                  hide-details="auto" class="dark-input"></v-text-field>
                              </div>
                              <div class="field">
                                <label class="field-label">Repeticiones</label>
                                <v-text-field v-model="ejercicio.repeticiones" outlined dense hide-details="auto"
                                  class="dark-input"></v-text-field>
                              </div>
                              <div class="field">
                                <label class="field-label">Descanso (seg)</label>
                                <v-text-field v-model="ejercicio.descanso_segundos" type="number" min="0" outlined
                                  dense hide-details="auto" class="dark-input"></v-text-field>
                              </div>
                            </div>
                            <div class="field">
                              <label class="field-label">Peso recomendado</label>
                              <v-text-field v-model="ejercicio.peso_recomendado" outlined dense hide-details="auto"
                                placeholder="Ej. 20kg" class="dark-input"></v-text-field>
                            </div>
                            <div class="field">
                              <label class="field-label">Notas</label>
                              <v-textarea v-model="ejercicio.notas" outlined dense rows="2" hide-details="auto"
                                placeholder="Indicaciones especiales…" class="dark-input"></v-textarea>
                            </div>
                          </div>
                        </div>
                      </draggable>

                      <div v-if="!ejerciciosPorDia[diaValue] || ejerciciosPorDia[diaValue].length === 0"
                        class="info-box">
                        <v-icon size="16" color="#8C8C93" class="mr-2">mdi-information-outline</v-icon>
                        No hay ejercicios agregados para este día
                      </div>
                    </div>
                  </v-tab-item>
                </v-tabs-items>
              </div>

              <div class="stepper-actions">
                <button class="btn-primary" @click="siguientePaso(3)">
                  Continuar
                  <v-icon size="15" class="ml-2">mdi-arrow-right</v-icon>
                </button>
                <button class="btn-ghost" @click="pasoActual = 2">
                  <v-icon size="15" class="mr-2">mdi-arrow-left</v-icon>
                  Atrás
                </button>
                <button class="btn-ghost" @click="cancelar">Cancelar</button>
              </div>
            </v-stepper-content>

            <!-- Paso 4 -->
            <v-stepper-content step="4">
              <div class="card-flat">
                <div class="card-flat-head">
                  <span class="section-eyebrow">
                    <span class="tick-mark"></span>
                    Revisar y guardar rutina
                  </span>
                </div>
                <div class="card-flat-body">
                  <span class="mini-eyebrow">Información general</span>
                  <div class="info-table mt-sm">
                    <div class="info-row">
                      <span class="info-key">Nombre</span>
                      <span class="info-val">{{ rutina.nombre }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-key">Objetivo</span>
                      <span class="info-val">{{ rutina.objetivo }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-key">Nivel</span>
                      <span class="info-val">{{ rutina.nivel_dificultad }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-key">Duración</span>
                      <span class="info-val">{{ rutina.duracion_semanas }} semanas</span>
                    </div>
                    <div class="info-row">
                      <span class="info-key">Descripción</span>
                      <span class="info-val">{{ rutina.descripcion || 'Sin descripción' }}</span>
                    </div>
                  </div>

                  <div class="section-divider"></div>

                  <span class="mini-eyebrow">Días de entrenamiento</span>
                  <v-expansion-panels class="dark-expansion mt-sm">
                    <v-expansion-panel v-for="diaValue in diasSeleccionados" :key="diaValue">
                      <v-expansion-panel-header>
                        <span class="expansion-title">{{ nombresDias[diaValue] || obtenerNombreDia(diaValue) }}</span>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <div v-if="ejerciciosPorDia[diaValue] && ejerciciosPorDia[diaValue].length > 0"
                          class="table-wrapper">
                          <table class="plain-table">
                            <thead>
                              <tr>
                                <th>Ejercicio</th>
                                <th>Series</th>
                                <th>Repeticiones</th>
                                <th>Descanso</th>
                                <th>Peso</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(ejercicio, index) in ejerciciosPorDia[diaValue]" :key="index">
                                <td class="cell-strong">{{ ejercicio.nombre }}</td>
                                <td class="cell-secondary">{{ ejercicio.series || '-' }}</td>
                                <td class="cell-secondary">{{ ejercicio.repeticiones || '-' }}</td>
                                <td class="cell-secondary">{{ ejercicio.descanso_segundos ? ejercicio.descanso_segundos + 's' : '-' }}</td>
                                <td class="cell-secondary">{{ ejercicio.peso_recomendado || '-' }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div v-else class="empty-inline">No hay ejercicios para este día</div>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </div>
              </div>

              <div class="stepper-actions">
                <button class="btn-primary" @click="guardarRutina" :disabled="guardando">
                  <span v-if="!guardando">
                    <v-icon size="15" class="mr-2">mdi-check</v-icon>
                    Guardar rutina
                  </span>
                  <span v-else class="loading-inline"><span class="spinner-sm"></span>Guardando</span>
                </button>
                <button class="btn-ghost" @click="pasoActual = 3">
                  <v-icon size="15" class="mr-2">mdi-arrow-left</v-icon>
                  Atrás
                </button>
                <button class="btn-ghost" @click="cancelar">Cancelar</button>
              </div>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </section>
    </div>

    <!-- Diálogo para seleccionar ejercicios -->
    <v-dialog v-model="dialogoEjercicios" max-width="700px">
      <div class="modal-panel">
        <div class="modal-head">
          <h3 class="modal-title">Seleccionar ejercicio</h3>
        </div>

        <div class="modal-body">
          <div class="search-input-wrapper mb-lg">
            <v-icon size="18" color="#5C5C62">mdi-magnify</v-icon>
            <input v-model="busquedaEjercicio" type="text" class="search-input" placeholder="Buscar ejercicios…" />
          </div>

          <div class="ejercicios-list">
            <div v-for="ejercicio in ejerciciosFiltrados" :key="ejercicio.id" class="ejercicio-item"
              @click="seleccionarEjercicio(ejercicio)">
              <img :src="ejercicio.imagen_url || 'https://via.placeholder.com/50?text=Ejercicio'"
                class="ejercicio-thumb" alt="Ejercicio" />
              <div class="flex-1">
                <div class="ejercicio-item-name">{{ ejercicio.nombre }}</div>
                <div class="ejercicio-item-cat">{{ ejercicio.categoria_nombre }}</div>
              </div>
              <v-icon size="18" color="#C8FF4D">mdi-plus</v-icon>
            </div>
          </div>

          <div v-if="ejerciciosFiltrados.length === 0" class="info-box">
            <v-icon size="16" color="#8C8C93" class="mr-2">mdi-information-outline</v-icon>
            No se encontraron ejercicios
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-ghost" @click="dialogoEjercicios = false">Cerrar</button>
        </div>
      </div>
    </v-dialog>

    <!-- Loading Overlay -->
    <div v-if="cargando" class="loading-overlay">
      <div class="loading-inner">
        <div class="spinner"></div>
        <p class="loading-text">Cargando…</p>
      </div>
    </div>

    <!-- Snackbar -->
    <v-snackbar v-model="mostrarMensaje" :timeout="3000" :color="mensaje.color" top right>
      <div class="flex items-center gap-md">
        <v-icon color="white">{{ mensajeIcon }}</v-icon>
        <span>{{ mensaje.texto }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon small @click="mostrarMensaje = false" v-bind="attrs">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import HttpService from '../../Servicios/HttpService'
import draggable from 'vuedraggable'

export default {
  name: "CreadorRutinas",
  components: { draggable },

  data: () => ({
    cargando: false,
    guardando: false,
    pasoActual: 1,
    tabDiaActual: null,
    dialogoEjercicios: false,
    diaSeleccionadoParaEjercicios: null,
    busquedaEjercicio: '',
    reglasRequerido: [(v) => !!v || 'Campo obligatorio'],

    rutina: {
      nombre: '',
      descripcion: '',
      nivel_dificultad: '',
      duracion_semanas: 4,
      objetivo: '',
      creada_por: 1
    },

    diasSeleccionados: [],
    nombresDias: {},
    ejerciciosPorDia: {},
    ejercicios: [],
    objetivos: [
      'Perdida_grasa', 'Ganancia_muscular', 'Fuerza', 'Resistencia', 'General'
    ],
    nivelesDificultad: ['principiante', 'intermedio', 'avanzado'],
    diasSemana: [
      { text: 'Lunes', value: 1 },
      { text: 'Martes', value: 2 },
      { text: 'Miércoles', value: 3 },
      { text: 'Jueves', value: 4 },
      { text: 'Viernes', value: 5 },
      { text: 'Sábado', value: 6 },
      { text: 'Domingo', value: 7 }
    ],

    mensaje: { texto: '', color: '' },
    mostrarMensaje: false
  }),

  computed: {
    mensajeIcon() {
      switch (this.mensaje.color) {
        case 'success': return 'mdi-check-circle'
        case 'error': return 'mdi-alert-circle'
        default: return 'mdi-information'
      }
    },

    ejerciciosFiltrados() {
      if (!this.busquedaEjercicio) return this.ejercicios;

      const searchLower = this.busquedaEjercicio.toLowerCase();
      return this.ejercicios.filter(ejercicio =>
        ejercicio.nombre.toLowerCase().includes(searchLower) ||
        (ejercicio.descripcion && ejercicio.descripcion.toLowerCase().includes(searchLower)) ||
        (ejercicio.categoria_nombre && ejercicio.categoria_nombre.toLowerCase().includes(searchLower))
      );
    }
  },

  mounted() {
    this.obtenerEjercicios();
    if (this.$route.params.id) {
      this.cargarRutinaParaEditar(this.$route.params.id)
    }
  },

  methods: {
    toggleDia(valor) {
      const index = this.diasSeleccionados.indexOf(valor);
      if (index >= 0) {
        this.diasSeleccionados.splice(index, 1);
      } else {
        this.diasSeleccionados.push(valor);
      }
    },

    async cargarRutinaParaEditar(id) {
      this.cargando = true;
      let payload = {
        metodo: "get_by_id",
        id: parseInt(id)
      };

      try {
        const rutina = await HttpService.obtenerConDatos(payload, "rutinas.php");

        if (rutina && rutina.success !== false) {
          this.rutina = {
            id: rutina.id,
            nombre: rutina.nombre,
            descripcion: rutina.descripcion,
            nivel_dificultad: rutina.nivel_dificultad,
            duracion_semanas: rutina.duracion_semanas,
            objetivo: rutina.objetivo,
            creada_por: rutina.creada_por
          };

          this.diasSeleccionados = rutina.dias.map(dia => dia.dia_semana);
          this.nombresDias = {};
          this.ejerciciosPorDia = {};

          rutina.dias.forEach(dia => {
            this.nombresDias[dia.dia_semana] = dia.nombre_dia;
            this.ejerciciosPorDia[dia.dia_semana] = dia.ejercicios.map(ej => ({
              id: ej.id,
              nombre: ej.ejercicio_nombre,
              categoria_nombre: ej.categoria_nombre,
              ejercicio_id: ej.ejercicio_id,
              series: ej.series,
              repeticiones: ej.repeticiones,
              peso_recomendado: ej.peso_recomendado,
              descanso_segundos: ej.descanso_segundos,
              orden: ej.orden,
              notas: ej.notas,
              configurado: true
            }));
          });

          this.pasoActual = 4;
        } else {
          throw new Error(rutina.message || "Rutina no encontrada");
        }
      } catch (error) {
        this.mostrarMensaje = true;
        this.mensaje = {
          texto: "❌ Error al cargar la rutina: " + error.message,
          color: "error"
        };
        console.error("Error:", error);
      } finally {
        this.cargando = false;
      }
    },

    async obtenerEjercicios() {
      this.cargando = true;
      let payload = { metodo: "get" };

      try {
        const respuesta = await HttpService.obtenerConDatos(payload, "ejercicios.php");
        this.ejercicios = respuesta;
      } catch (error) {
        this.mostrarMensaje = true;
        this.mensaje = {
          texto: "❌ Error al cargar los ejercicios",
          color: "error"
        };
        console.error("Error al obtener ejercicios:", error);
      } finally {
        this.cargando = false;
      }
    },

    siguientePaso(paso) {
      if (paso === 1) {
        if (this.$refs.formPaso1.validate()) {
          this.pasoActual = 2;
        }
      } else if (paso === 2) {
        this.diasSeleccionados.forEach(dia => {
          if (!this.ejerciciosPorDia[dia]) {
            this.$set(this.ejerciciosPorDia, dia, []);
          }
          if (!this.nombresDias[dia]) {
            this.nombresDias[dia] = this.obtenerNombrePorDefecto(dia);
          }
        });
        this.pasoActual = 3;
      } else if (paso === 3) {
        this.pasoActual = 4;
      }
    },

    obtenerNombreDia(valor) {
      const dia = this.diasSemana.find(d => d.value === valor);
      return dia ? dia.text : `Día ${valor}`;
    },

    obtenerNombrePorDefecto(valor) {
      const nombresPorDefecto = {
        1: 'Día de Piernas',
        2: 'Día de Pecho',
        3: 'Día de Espalda',
        4: 'Día de Hombros',
        5: 'Día de Brazos',
        6: 'Día de Cardio',
        7: 'Día de Descanso Activo'
      };
      return nombresPorDefecto[valor] || `Día ${valor}`;
    },

    mostrarDialogoEjercicios(diaValue) {
      if (!this.ejerciciosPorDia[diaValue]) {
        this.$set(this.ejerciciosPorDia, diaValue, []);
      }
      this.diaSeleccionadoParaEjercicios = diaValue;
      this.dialogoEjercicios = true;
      this.busquedaEjercicio = '';
    },

    seleccionarEjercicio(ejercicio) {
      const ejercicioConfigurado = {
        ...ejercicio,
        ejercicio_id: ejercicio.id,
        series: 3,
        repeticiones: '10-12',
        descanso_segundos: 60,
        peso_recomendado: '',
        notas: '',
        configurado: true,
        orden: this.ejerciciosPorDia[this.diaSeleccionadoParaEjercicios].length
      };

      this.ejerciciosPorDia[this.diaSeleccionadoParaEjercicios].push(ejercicioConfigurado);
      this.dialogoEjercicios = false;
    },

    editarEjercicioDia(diaValue, index) {
      this.ejerciciosPorDia[diaValue][index].configurado =
        !this.ejerciciosPorDia[diaValue][index].configurado;
    },

    eliminarEjercicioDia(diaValue, index) {
      this.ejerciciosPorDia[diaValue].splice(index, 1);
      this.reordenarEjercicios(diaValue);
    },

    reordenarEjercicios(diaValue) {
      this.ejerciciosPorDia[diaValue].forEach((ejercicio, index) => {
        ejercicio.orden = index;
      });
    },

    async guardarRutina() {
      this.guardando = true;

      const rutinaCompleta = {
        ...this.rutina,
        dias: this.diasSeleccionados.map(diaValue => ({
          dia_semana: diaValue,
          nombre_dia: this.nombresDias[diaValue] || this.obtenerNombreDia(diaValue),
          ejercicios: this.ejerciciosPorDia[diaValue].map(ej => ({
            ejercicio_id: ej.ejercicio_id || ej.id,
            series: ej.series,
            repeticiones: ej.repeticiones,
            peso_recomendado: ej.peso_recomendado,
            descanso_segundos: ej.descanso_segundos,
            orden: ej.orden,
            notas: ej.notas
          }))
        }))
      };

      let payload = {
        metodo: "post",
        rutina: rutinaCompleta
      };

      try {
        const respuesta = await HttpService.registrar(payload, "rutinas.php");

        if (respuesta && respuesta.success) {
          this.mostrarMensaje = true;
          this.mensaje = {
            texto: "✅ Rutina creada exitosamente",
            color: "success"
          };

          setTimeout(() => {
            this.resetearFormulario();
            this.$router.push('/rutinas');
          }, 2000);
        }
      } catch (error) {
        this.mostrarMensaje = true;
        this.mensaje = {
          texto: "❌ Error al guardar la rutina",
          color: "error"
        };
        console.error("Error al guardar rutina:", error);
      } finally {
        this.guardando = false;
      }
    },

    resetearFormulario() {
      this.pasoActual = 1;
      this.rutina = {
        nombre: '',
        descripcion: '',
        nivel_dificultad: '',
        duracion_semanas: 4,
        objetivo: '',
        creada_por: 1
      };
      this.diasSeleccionados = [];
      this.nombresDias = {};
      this.ejerciciosPorDia = {};
      if (this.$refs.formPaso1) {
        this.$refs.formPaso1.reset();
      }
    },

    cancelar() {
      this.$router.go(-1);
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap');

/* ===== TOKENS ===== */
.creador-page {
  --bg: #0E0E10;
  --card: #17171A;
  --card-border: #26262A;
  --text-primary: #F2F2ED;
  --text-secondary: #8C8C93;
  --text-tertiary: #5C5C62;
  --accent: #C8FF4D;
  --accent-dim: rgba(200, 255, 77, 0.12);
  --error: #FF6B6B;
  --radius: 4px;

  --font-display: 'Space Grotesk', sans-serif;
  --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: 'JetBrains Mono', monospace;
}

.creador-page {
  min-height: 100vh;
  background: var(--bg);
  color: var(--text-primary);
  font-family: var(--font-body);
  position: relative;
  padding: 40px;
}

.bg-grid {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(rgba(242, 242, 237, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(242, 242, 237, 0.02) 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
  z-index: 0;
}

.creador-shell {
  position: relative;
  z-index: 1;
  max-width: 900px;
  margin: 0 auto;
}

/* ===== HEADER ===== */
.page-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 22px 26px;
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  margin-bottom: 20px;
}

.page-icon-box {
  width: 42px;
  height: 42px;
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.page-title {
  font-family: var(--font-display);
  font-size: 19px;
  font-weight: 700;
  margin: 0 0 2px;
}

.page-sub {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

/* ===== STEPPER ===== */
.dark-stepper {
  background: transparent !important;
  box-shadow: none !important;
}

.dark-stepper-header {
  background: var(--card) !important;
  border: 1px solid var(--card-border) !important;
  border-radius: var(--radius) !important;
  box-shadow: none !important;
  margin-bottom: 20px;
}

.dark-stepper >>> .v-stepper__step__step {
  background: var(--card) !important;
  border: 1px solid var(--card-border) !important;
  color: var(--text-secondary) !important;
  box-shadow: none !important;
}

.dark-stepper >>> .v-stepper__step--active .v-stepper__step__step {
  background: var(--accent) !important;
  border-color: var(--accent) !important;
  color: var(--bg) !important;
}

.dark-stepper >>> .v-stepper__step--complete .v-stepper__step__step {
  background: var(--accent-dim) !important;
  border-color: var(--accent) !important;
  color: var(--accent) !important;
}

.dark-stepper >>> .v-stepper__step__step .v-icon {
  color: var(--bg) !important;
}

.dark-stepper >>> .v-stepper__label {
  color: var(--text-primary) !important;
  font-family: var(--font-body) !important;
  font-size: 12.5px !important;
}

.dark-stepper >>> .v-stepper__step--active .v-stepper__label {
  color: var(--accent) !important;
}

.dark-stepper >>> .v-divider {
  border-color: var(--card-border) !important;
}

.dark-stepper >>> .v-stepper__content {
  padding: 0 !important;
}

.dark-stepper >>> .v-stepper__wrapper {
  background: transparent !important;
}

/* ===== CARD ===== */
.card-flat {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
}

.card-flat-nopad {
  overflow: hidden;
}

.card-flat-head {
  padding: 18px 24px;
  border-bottom: 1px solid var(--card-border);
}

.section-eyebrow {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-secondary);
}

.tick-mark {
  width: 12px;
  height: 1px;
  background: var(--accent);
}

.card-flat-body {
  padding: 24px;
}

.body-text {
  font-size: 13.5px;
  color: var(--text-secondary);
  margin: 0;
}

/* ===== STEPPER ACTIONS ===== */
.stepper-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
  flex-wrap: wrap;
}

/* ===== FIELDS ===== */
.dark-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.field-row-3 {
  grid-template-columns: 1fr 1fr 1fr;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--text-secondary);
  text-transform: uppercase;
}

.mini-eyebrow {
  display: block;
  font-family: var(--font-mono);
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-tertiary);
}

.section-divider {
  height: 1px;
  background: var(--card-border);
  margin: 20px 0;
}

/* ===== CHIPS ===== */
.chip-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  background: transparent;
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  color: var(--text-secondary);
  font-size: 13px;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.filter-chip:hover {
  border-color: var(--text-tertiary);
  color: var(--text-primary);
}

.filter-chip-active {
  border-color: var(--accent);
  color: var(--accent);
  background: var(--accent-dim);
}

.info-box {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 12px 14px;
  font-size: 12.5px;
  color: var(--text-secondary);
}

/* ===== TABS ===== */
.dark-tabs {
  background: transparent !important;
  border-bottom: 1px solid var(--card-border);
}

.dark-tabs >>> .v-tabs-bar {
  background: transparent !important;
}

.dark-tabs >>> .v-tab {
  color: var(--text-secondary) !important;
  text-transform: none !important;
  font-weight: 500 !important;
  font-size: 13px !important;
}

.dark-tabs >>> .v-tab--active {
  color: var(--accent) !important;
}

.dark-tabs >>> .v-tabs-slider {
  background: var(--accent) !important;
}

.dark-tabs-items {
  background: transparent !important;
}

.tab-body {
  padding: 20px 24px;
}

/* ===== EJERCICIO CARD ===== */
.ejercicio-card {
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 14px 16px;
  margin-bottom: 10px;
}

.ejercicio-head {
  display: flex;
  align-items: center;
}

.ejercicio-name {
  font-size: 13.5px;
  color: var(--text-primary);
  font-weight: 600;
}

.ejercicio-cat {
  font-size: 11px;
  color: var(--text-tertiary);
  margin-top: 1px;
}

.ejercicio-config {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--card-border);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.icon-btn {
  width: 28px;
  height: 28px;
  border-radius: var(--radius);
  background: transparent;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.icon-btn:hover {
  border-color: var(--card-border);
  background: rgba(255, 255, 255, 0.03);
}

.icon-btn-danger:hover {
  border-color: rgba(255, 107, 107, 0.3);
  background: rgba(255, 107, 107, 0.06);
}

/* ===== INFO TABLE ===== */
.info-table {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
}

.info-key {
  font-size: 12px;
  color: var(--text-tertiary);
  flex-shrink: 0;
}

.info-val {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text-primary);
  text-align: right;
}

/* ===== EXPANSION PANELS ===== */
.dark-expansion {
  background: transparent !important;
}

.dark-expansion >>> .v-expansion-panel {
  background: var(--bg) !important;
  border: 1px solid var(--card-border) !important;
  border-radius: var(--radius) !important;
  margin-bottom: 8px;
}

.dark-expansion >>> .v-expansion-panel::before {
  box-shadow: none !important;
}

.dark-expansion >>> .v-expansion-panel-header {
  padding: 12px 16px !important;
  min-height: 44px !important;
}

.dark-expansion >>> .v-expansion-panel-header .v-icon {
  color: var(--text-tertiary) !important;
}

.dark-expansion >>> .v-expansion-panel-content__wrap {
  padding: 0 16px 16px !important;
}

.expansion-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.empty-inline {
  padding: 16px;
  text-align: center;
  font-size: 12px;
  color: var(--text-tertiary);
}

/* ===== DETAIL TABLE ===== */
.table-wrapper {
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  overflow: hidden;
}

.plain-table {
  width: 100%;
  border-collapse: collapse;
}

.plain-table th {
  text-align: left;
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-tertiary);
  background: rgba(255, 255, 255, 0.02);
  padding: 8px 12px;
  border-bottom: 1px solid var(--card-border);
}

.plain-table td {
  padding: 8px 12px;
  border-bottom: 1px solid var(--card-border);
}

.plain-table tr:last-child td {
  border-bottom: none;
}

.cell-strong {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.cell-secondary {
  font-size: 12.5px;
  color: var(--text-secondary);
}

/* ===== MODAL: SELECCIONAR EJERCICIO ===== */
.modal-panel {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  overflow: hidden;
}

.modal-head {
  padding: 22px 24px;
  border-bottom: 1px solid var(--card-border);
}

.modal-title {
  font-family: var(--font-display);
  font-size: 17px;
  font-weight: 700;
  margin: 0;
}

.modal-body {
  padding: 24px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid var(--card-border);
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 0 14px;
  transition: border-color 0.15s ease;
}

.search-input-wrapper:focus-within {
  border-color: var(--accent);
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  padding: 11px 0;
  color: var(--text-primary);
  font-size: 13.5px;
  font-family: var(--font-body);
}

.search-input::placeholder {
  color: var(--text-tertiary);
}

.ejercicios-list {
  max-height: 360px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ejercicio-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  border-radius: var(--radius);
  cursor: pointer;
  transition: background 0.15s ease;
}

.ejercicio-item:hover {
  background: rgba(255, 255, 255, 0.03);
}

.ejercicio-thumb {
  width: 42px;
  height: 42px;
  border-radius: var(--radius);
  object-fit: cover;
  border: 1px solid var(--card-border);
}

.ejercicio-item-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.ejercicio-item-cat {
  font-size: 11px;
  color: var(--text-tertiary);
}

/* ===== BUTTONS ===== */
.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--accent);
  color: var(--bg);
  border: none;
  border-radius: var(--radius);
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-primary:hover:not(:disabled) {
  filter: brightness(1.08);
}

.btn-primary:disabled {
  opacity: 0.45;
  cursor: default;
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.btn-ghost:hover {
  border-color: var(--text-tertiary);
  color: var(--text-primary);
}

.loading-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.spinner-sm {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(14, 14, 16, 0.3);
  border-top-color: var(--bg);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

/* ===== LOADING ===== */
.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(14, 14, 16, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
}

.loading-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 2px solid var(--card-border);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.loading-text {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--text-secondary);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ===== MOTION ===== */
.fade-in {
  animation: fadeIn 0.4s ease both;
}

.delay-100 { animation-delay: 0.05s; }

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .fade-in, .spinner, .spinner-sm { animation: none; }
}

/* ===== Overrides Vuetify (v-text-field, v-select, v-textarea) ===== */
.dark-input >>> .v-input__slot {
  background: var(--card) !important;
  border-radius: 4px !important;
  box-shadow: none !important;
  min-height: 42px !important;
}

.dark-input >>> fieldset {
  border-color: var(--card-border) !important;
  border-width: 1px !important;
  border-radius: 4px !important;
}

.dark-input >>> .v-input--is-focused fieldset {
  border-color: var(--accent) !important;
  border-width: 1.5px !important;
}

.dark-input >>> input,
.dark-input >>> textarea,
.dark-input >>> .v-select__selection {
  color: var(--text-primary) !important;
  font-family: var(--font-body) !important;
  font-size: 13.5px !important;
  caret-color: var(--accent) !important;
  text-transform: none;
}

.dark-input >>> .v-icon {
  color: var(--text-tertiary) !important;
}

.dark-input >>> .v-messages {
  color: var(--error) !important;
}

.creador-page >>> .v-menu__content {
  background: var(--card) !important;
  border: 1px solid var(--card-border) !important;
  border-radius: 4px !important;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5) !important;
}

.creador-page >>> .v-list {
  background: transparent !important;
  padding: 4px !important;
}

.creador-page >>> .v-list-item {
  border-radius: 4px !important;
  text-transform: capitalize;
}

.creador-page >>> .v-list-item__title {
  color: var(--text-primary) !important;
  font-family: var(--font-body) !important;
  font-size: 13px !important;
}

.creador-page >>> .v-list-item:hover {
  background: rgba(255, 255, 255, 0.04) !important;
}

.creador-page >>> .v-list-item--active {
  background: var(--accent-dim) !important;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .creador-page {
    padding: 20px;
  }

  .field-row,
  .field-row-3 {
    grid-template-columns: 1fr;
  }

  .stepper-actions .btn-primary,
  .stepper-actions .btn-ghost {
    flex: 1;
  }

  .dark-stepper >>> .v-stepper__label {
    display: none !important;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Planes/Rutinas.vue